import type { NotificationHubTemplateBlockInput } from '../../../../../../__generated__/graphql/types';
import { NotificationHubTemplateBlockTypeEnum } from '../../../../../../__generated__/graphql/types';
import type { TEmailEditorBlock } from '../../../mail-canvas/blocks/block-types';
import { TEAM_METRICS_CATALOG } from '../../team-metrics/team-metrics-catalog';
import type { TTeamMetricId } from '../../team-metrics/types';

type TMetricLabelResolver = (metricId: TTeamMetricId) => string;

/**
 * `props` is validated server-side against the block catalog signature
 * (`NotificationHubTemplateBlockInput.props` doc comment). Most field names
 * used here (`html`/`align`, `texto`/`href`/`variant`, `alt`/`width`,
 * `items`/`token`/`label`/`display`) mirror this repo's own FE-owned catalog
 * (`catalogo/texto.ts`, `boton.ts`, `imagen.ts`, `metricas.ts`). The IMAGE
 * block's file id is the one exception: `catalogo/imagen.ts` names it
 * `fileId`, but the real backend contract Hans confirmed uses `file_id`
 * (snake_case) — `plantillas/CLAUDE.md`, "IMAGE exige file_id". `props` is a
 * scalar JSON the backend validates by key, so a mismatched name compiles,
 * passes every test, and only fails when the email renders.
 * `[no verificado]` against the rest of the real backend catalog signature.
 */
const findMetricLabel = (
  metricId: TTeamMetricId,
  resolveLabel: TMetricLabelResolver,
): string => {
  const isKnownMetric = TEAM_METRICS_CATALOG.some(({ id }) => id === metricId);

  return isKnownMetric ? resolveLabel(metricId) : metricId;
};

export const templateBlockToInput = (
  block: TEmailEditorBlock,
  resolveMetricLabel: TMetricLabelResolver,
): NotificationHubTemplateBlockInput | undefined => {
  if (block.type === 'TEXT') {
    return {
      id: block.id,
      type: NotificationHubTemplateBlockTypeEnum.Text,
      props: { html: block.html, align: 'left' },
    };
  }

  if (block.type === 'BUTTON') {
    return {
      id: block.id,
      type: NotificationHubTemplateBlockTypeEnum.Button,
      props: {
        texto: block.text,
        href: block.link,
        align: 'center',
        variant: 'primary',
      },
    };
  }

  if (block.type === 'IMAGE') {
    if (!block.fileId) {
      return undefined;
    }

    return {
      id: block.id,
      type: NotificationHubTemplateBlockTypeEnum.Image,
      props: { file_id: block.fileId, alt: '', width: '600' },
    };
  }

  if (block.type === 'DIVIDER') {
    return {
      id: block.id,
      type: NotificationHubTemplateBlockTypeEnum.Divider,
      props: {},
    };
  }

  return {
    id: block.id,
    type: NotificationHubTemplateBlockTypeEnum.Metrics,
    props: {
      items: JSON.stringify(
        block.metricIds.map(metricId => ({
          token: metricId,
          label: findMetricLabel(metricId, resolveMetricLabel),
          display: 'bar',
        })),
      ),
    },
  };
};
